import { Editor, Tldraw, TLUserPreferences, useTldrawUser, useEditor, useValue } from 'tldraw';
import 'tldraw/tldraw.css';
import './meetingBoard.scss';
import { useMultiplayerSync } from '@/hooks/useMultiplayerSync';
import { useUserStore } from '@/stores/user.ts';
import { ParticipantRole } from '@/consts/meeting.ts';
import { useStudyModeStore } from '@/stores/studyMode.ts';
import { useEffect, useState } from 'react';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useBoardStore } from '@/stores/board.ts';
import api from '@/api/api.ts';
import { useSend } from '@/components/SendProvider';
import { EVENTS } from '@/consts/events.ts';
import clsx from 'clsx';
import { tldrawLicenseKey } from '@/consts/license.ts';
import { getAssetUrlsByImport } from '@tldraw/assets/imports.vite';

const assetUrls = getAssetUrlsByImport();

interface ViewedBoardProps {
  id: string;
  setActiveId: (id: string) => void;
}

interface MeetingBoardProps extends ViewedBoardProps {
  activeId: string | null;
}

interface MyBoardProps {
  id: string;
  activeId: string | null;
}

// This component renders the user's own board
// It's always mounted, but hidden when the user is viewing other boards
function MyBoard({ id, activeId }: MyBoardProps) {
  const userUid = useUserStore((state) => state.userUid);
  const role = useUserStore((state) => state.role);
  const userName = useUserStore((state) => state.userName);
  const meeting = useMeetingStore((state) => state.meeting);
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const { sendBoardPageChange } = useSend();

  const { currentUserEditor, setCurrentUserEditor } = useBoardStore.getState();

  const [userPreferences] = useState<TLUserPreferences>({
    id: userUid,
    name: userName
  });

  const roomId = `tldraw-meeting-${meeting?.uuid}-user-${userUid}`;
  const store = useMultiplayerSync(roomId, userPreferences);
  const user = useTldrawUser({ userPreferences });

  useEffect(() => {
    if (!currentUserEditor) return;
    if (activeId !== id) {
      currentUserEditor.blur();
      if (!isPracticeMode) {
        currentUserEditor.updateDocumentSettings({
          meta: { ...currentUserEditor.getDocumentSettings().meta, isFocused: false }
        });
      }
    } else {
      currentUserEditor.focus();
      if (!isPracticeMode) {
        currentUserEditor.updateDocumentSettings({
          meta: { ...currentUserEditor.getDocumentSettings().meta, isFocused: true }
        });
      }
    }
  }, [currentUserEditor, activeId]);

  const handleMount = (editor: Editor) => {
    setCurrentUserEditor(editor);

    if (role === ParticipantRole.TEACHER) {
      // when the teacher changes the page, send an event to all the students
      const pageChangeCleanup = editor.sideEffects.registerAfterChangeHandler(
        'instance',
        (prev, next) => {
          // only send an update if the page has actually changed
          if (isPracticeMode) return;
          if (prev.currentPageId === next.currentPageId) return;

          const message = {
            action: EVENTS.BOARD_PAGE_CHANGE,
            pageId: next.currentPageId
          };
          sendBoardPageChange(new TextEncoder().encode(JSON.stringify(message)), {
            reliable: true
          });
        }
      );
      return () => pageChangeCleanup();
    }
  };

  const maxPages = role === ParticipantRole.STUDENT ? 5 : undefined;

  return (
    <Tldraw
      assetUrls={assetUrls}
      licenseKey={tldrawLicenseKey}
      store={store}
      user={user}
      onMount={handleMount}
      options={{ maxPages }}
      components={useCollaborationComponents()}
    />
  );
}

// This component renders the board of the user being viewed
// It's only mounted when `currentlyViewingUserUid` is set to another user
function ViewedBoard({ id, setActiveId }: ViewedBoardProps) {
  const userUid = useUserStore((state) => state.userUid);
  const role = useUserStore((state) => state.role);
  const userName = useUserStore((state) => state.userName);

  const meeting = useMeetingStore((state) => state.meeting);
  const currentlyViewingUserUid = useMeetingStore((state) => state.currentlyViewingUserUid);

  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);

  const { setEditor } = useBoardStore.getState();

  const [userPreferences] = useState<TLUserPreferences>({
    id: userUid,
    name: userName
  });

  const roomId = `tldraw-meeting-${meeting?.uuid}-user-${isPracticeMode ? currentlyViewingUserUid : meeting?.author_uuid}`;
  const store = useMultiplayerSync(roomId, userPreferences);
  const user = useTldrawUser({ userPreferences });

  const isReadonly = role === ParticipantRole.STUDENT;

  const handleMount = (editor: Editor) => {
    setEditor(editor);

    editor.sideEffects.registerBeforeDeleteHandler('shape', (_, source) => {
      if (source === 'remote') return;
      if (isReadonly) return false;
      return;
    });

    editor.sideEffects.registerBeforeChangeHandler('shape', (prev, next, source) => {
      if (source === 'remote') return next;
      if (isReadonly) return prev;
      return next;
    });
  };

  return (
    <Tldraw
      assetUrls={assetUrls}
      licenseKey={tldrawLicenseKey}
      store={store}
      user={user}
      onMount={handleMount}
      hideUi={isReadonly}
      options={{ enableToolbarKeyboardShortcuts: !isReadonly }}
      components={{
        ...(isReadonly
          ? {
              Toolbar: null,
              ContextMenu: null,
              ActionsMenu: null,
              MainMenu: null,
              StylePanel: null,
              KeyboardShortcutsDialog: null,
              TopPanel: null,
              QuickActions: null,
              HelperButtons: null,
              SelectionForeground: null
            }
          : {}),
        ...useCollaborationComponents()
      }}
      acceptedImageMimeTypes={isReadonly ? [] : ['image/jpeg', 'image/png']}
      acceptedVideoMimeTypes={[]}
      maxAssetSize={10 * 1024 * 1024}
    >
      <IsFocusedWatcher id={id} setActiveId={setActiveId} />
    </Tldraw>
  );
}

export function IsFocusedWatcher({ id, setActiveId }: ViewedBoardProps) {
  const editor = useEditor();
  const docSettingsMetaIsFocused = useValue(
    'documentSettingsMetaIsFocused',
    () => editor.getDocumentSettings().meta?.isFocused,
    [editor]
  );

  useEffect(() => {
    if (docSettingsMetaIsFocused) {
      setActiveId(id);
    }
  }, [docSettingsMetaIsFocused]);

  return null;
}

function MeetingBoard({ id, activeId, setActiveId }: MeetingBoardProps) {
  const role = useUserStore((state) => state.role);
  const userUid = useUserStore((state) => state.userUid);

  const meeting = useMeetingStore((state) => state.meeting);
  const currentlyViewingUserUid = useMeetingStore((state) => state.currentlyViewingUserUid);

  const currentUserEditor = useBoardStore((state) => state.currentUserEditor);
  const { setEditor, setCurrentUserEditor } = useBoardStore.getState();

  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);

  const isTeacherOrAssistant =
    role === ParticipantRole.TEACHER || role === ParticipantRole.ASSISTANT;

  const isViewingOtherBoard =
    (!isPracticeMode && role !== ParticipantRole.TEACHER) ||
    (isPracticeMode && isTeacherOrAssistant && !!currentlyViewingUserUid);

  const isActive = activeId === id;

  // This effect ensures the editor is correctly switched back to the
  // user's own editor when they stop viewing someone else's
  useEffect(() => {
    if (!isViewingOtherBoard && currentUserEditor) {
      setEditor(currentUserEditor);
    }
  }, [isViewingOtherBoard, currentUserEditor, setEditor]);

  // TODO: save the board every N seconds instead of relying on popstate and pagehide
  // save on tab close/hide or back button press for the user's own board
  useEffect(() => {
    if (!currentUserEditor || !meeting?.uuid || !userUid) {
      return;
    }

    const handlePageHide = () => {
      const pages = currentUserEditor.getPages();
      for (const page of pages) {
        const shapeIds = currentUserEditor.getPageShapeIds(page.id);
        if (shapeIds.size === 0) continue;

        currentUserEditor
          .toImage([...shapeIds], { format: 'png', scale: 1, background: true })
          .then((imageBlob) => {
            if (imageBlob) {
              api.uploadBoardBeacon(meeting.uuid, userUid, imageBlob.blob, page.index);
            }
          })
          .catch((e) => {
            console.error(`Board page ${page.index} upload failed on page hide:`, e);
          });
      }
    };

    window.addEventListener('popstate', handlePageHide);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('popstate', handlePageHide);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [currentUserEditor, meeting, userUid]);

  useEffect(() => {
    return () => {
      setEditor(null);
      setCurrentUserEditor(null);
    };
  }, [setEditor, setCurrentUserEditor]);

  if (!meeting) {
    return null;
  }

  return (
    <section className="board">
      <div
        className={clsx('board__wrapper', isActive && 'active')}
        onPointerDown={() => setActiveId(id)}
        style={{ display: isViewingOtherBoard ? 'none' : 'block' }}
      >
        <MyBoard id={id} activeId={activeId} />
      </div>
      {isViewingOtherBoard && (
        <div className={clsx('board__wrapper', isActive && 'active')}>
          <ViewedBoard id={id} setActiveId={setActiveId} />
        </div>
      )}
    </section>
  );
}

const useCollaborationComponents = () => {
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);

  return !isPracticeMode
    ? {
        CollaboratorBrush: null,
        CollaboratorCursor: null,
        CollaboratorHint: null,
        CollaboratorScribble: null,
        CollaboratorShapeIndicator: null
      }
    : {};
};

export default MeetingBoard;
